// src/modules/Menu/Menu.js
import React, { useEffect, useState, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box, CssBaseline, Typography, Toolbar, IconButton, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, AppBar as MuiAppBar, Drawer as MuiDrawer, Avatar, Menu, MenuItem,
  Divider, Tooltip, useMediaQuery, Paper, Badge, Popover, Chip, Collapse, Button
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import DomainIcon from '@mui/icons-material/Domain';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import LogoutIcon from '@mui/icons-material/Logout';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import HistoryIcon from '@mui/icons-material/History';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PaymentsIcon from '@mui/icons-material/Payments';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Grow from '@mui/material/Grow';
import axios from 'axios';

import { useAuth } from '../Login/AuthContext';
import ConfirmationModal from '../../components/ConfirmationModal';
import CompanySelectorModal from '../../components/CompanySelectorModal';
import ModalDispositivosSeguros from '../../components/ModalDispositivosSeguros';
import SessionGuard from '../../components/SessionGuard/SessionGuard';
import DevicesIcon from '@mui/icons-material/Devices';

import config from '../../config/config';
import { Items } from '../Menu/Items';

const drawerWidth = 270;
const apiUrl = config.apiBaseUrl;

const getShortEmpresaName = (name = '') => {
  const norm = String(name).trim().toUpperCase();
  if (norm === 'TODAS' || norm.includes('TODAS')) return 'TODAS';
  if (norm.includes('OFFSHORE')) return 'OFFSHORE';
  if (norm.includes('CONSTRUCC')) return 'CONSTRUCCIONES';
  if (norm.includes('PESADO')) return 'PESADO';
  return name || 'Empresa';
};

const getEmpresaColorStyle = (name = '') => {
  const norm = String(name).trim().toUpperCase();
  if (norm.includes('OFFSHORE')) {
    return {
      bgcolor: '#e6f0fa',
      color: '#005eb8',
      borderColor: '#b8daff',
      iconColor: '#005eb8',
    };
  }
  if (norm.includes('CONSTRUCC')) {
    return {
      bgcolor: '#f5ebd9',
      color: '#5c3317',
      borderColor: '#e2ccb2',
      iconColor: '#5c3317',
    };
  }
  if (norm.includes('PESADO')) {
    return {
      bgcolor: '#fcefdc',
      color: '#6c3400',
      borderColor: '#f5d3a5',
      iconColor: '#6c3400',
    };
  }
  if (norm === 'TODAS' || norm.includes('TODAS')) {
    return {
      bgcolor: '#f0fdf4',
      color: '#15803d',
      borderColor: '#bbf7d0',
      iconColor: '#15803d',
    };
  }
  return {
    bgcolor: '#f8fafc',
    color: '#1e293b',
    borderColor: '#cbd5e1',
    iconColor: '#0655A5',
  };
};

/* ---- mixins solo para DESKTOP (rail mini vs expandido) ---- */
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(8)} + 1px)`,
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 0.75),
  minHeight: 38,
  [theme.breakpoints.up('sm')]: {
    minHeight: 40,
  },
}));

/* ─── Token de color único para toda la chrome (Navbar + Sidebar) ─── */
const CHROME_BG = '#e8ecf2';

/* ---- AppBar acoplable ---- */
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'dockWidth',
})(({ theme, dockWidth }) => ({
  backgroundColor: CHROME_BG,
  color: '#1e293b',
  boxShadow: 'none',
  borderBottom: '1px solid #d5dbdf',
  minHeight: 40,
  '& .MuiToolbar-root': {
    minHeight: '40px !important',
    paddingLeft: '12px !important',
    paddingRight: '16px !important',
  },
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(dockWidth && dockWidth !== '0px' && {
    marginLeft: dockWidth,
    width: `calc(100% - ${dockWidth})`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export default function MenuDrawer() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isWideDesktop = useMediaQuery(theme.breakpoints.up('xl'));

  const [open, setOpen] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [menuItems, setMenuItems] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    general: true,
    operacion: true,
    reportes: false,
    catalogos: false,
  });

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [userCorreo, setUserCorreo] = useState('');
  const [userPerfil, setUserPerfil] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [empresaChangedAlert, setEmpresaChangedAlert] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElCompany, setAnchorElCompany] = useState(null);
  const [dispositivosModalOpen, setDispositivosModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [empresaRenderKey, setEmpresaRenderKey] = useState(0);

  const openMenu = Boolean(anchorEl);
  const openCompanyMenu = Boolean(anchorElCompany);

  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();

  // 🔔 ESTADO DE NOTIFICACIONES
  const [notificaciones, setNotificaciones] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorElNotif, setAnchorElNotif] = useState(null);
  const usuarioId = sessionStorage.getItem('UsuarioId');

  // ✅ Canal multi-tab (si el navegador lo soporta)
  const bcRef = useRef(null);

  useEffect(() => {
    try {
      bcRef.current = new BroadcastChannel('auth_channel');
    } catch {
      bcRef.current = null;
    }

    return () => {
      try {
        bcRef.current?.close?.();
      } catch {}
    };
  }, []);

  // ✅ Si otra pestaña avisa logout -> limpiamos y mandamos al login
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'APP_LOGOUT' && e.newValue) {
        sessionStorage.clear();
        navigate('/', { replace: true });
      }
    };

    const onBC = (ev) => {
      if (ev?.data?.type === 'LOGOUT') {
        sessionStorage.clear();
        navigate('/', { replace: true });
      }
    };

    window.addEventListener('storage', onStorage);
    try {
      bcRef.current?.addEventListener?.('message', onBC);
    } catch {}

    return () => {
      window.removeEventListener('storage', onStorage);
      try {
        bcRef.current?.removeEventListener?.('message', onBC);
      } catch {}
    };
  }, [navigate]);

  // ✅ Heartbeat global: mantiene el estatus "En Línea" activo en tiempo real
  useEffect(() => {
    const sendGlobalHeartbeat = () => {
      let uId = sessionStorage.getItem('UsuarioId');
      try {
        if (uId && uId.startsWith('"')) uId = JSON.parse(uId);
      } catch (_) {}
      const uName = sessionStorage.getItem('username') || sessionStorage.getItem('loggedInUsername');
      if (apiUrl && (uId || uName)) {
        axios.post(`${apiUrl}/api/usuario/heartbeat`, { usuarioId: uId, username: uName }).catch(() => {});
      }
    };

    sendGlobalHeartbeat();
    const heartbeatTimer = setInterval(sendGlobalHeartbeat, 60000);
    return () => clearInterval(heartbeatTimer);
  }, []);

  const dockWidth = isDesktop
    ? (open ? `${drawerWidth}px` : `calc(${theme.spacing(8)} + 1px)`)
    : '0px';

  const handleDrawerClose = () => setOpen(false);

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleOpenConfirmationModal = () => {
    setConfirmationModalOpen(true);
    handleMenuClose();
  };

  const handleCloseConfirmationModal = () => setConfirmationModalOpen(false);

  const handleItemClick = (path) => {
    navigate(path);
    if (!isDesktop) setOpen(false);
  };

  // ✅ LOGOUT “de verdad”: limpia sesión + avisa a otras pestañas
  const handleLogout = () => {
    try {
      handleCloseConfirmationModal();
      handleMenuClose();
      setCompanyModalOpen(false);

      logout?.();

      sessionStorage.clear();

      // limpiar llaves viejas/locales si las hubiera
      localStorage.removeItem('filterComponent.visibleFilters');

      try {
        bcRef.current?.postMessage?.({ type: 'LOGOUT', at: Date.now() });
      } catch {}

      try {
        localStorage.setItem('APP_LOGOUT', String(Date.now()));
      } catch {}
    } catch (e) {
      console.error('❌ Error en logout:', e);
    } finally {
      navigate('/', { replace: true });
    }
  };

  // Abrir desplegable rápido de cambio de empresa desde la barra superior
  const handleQuickCompanyClick = async (event) => {
    const target = event.currentTarget;
    const usuarioId = JSON.parse(sessionStorage.getItem('UsuarioId') || 'null');
    if (!usuarioId) return;

    try {
      const res = await fetch(`${apiUrl}/api/empresas/${usuarioId}`);
      const data = await res.json();

      if (data.empresas && Array.isArray(data.empresas)) {
        const rawCompanies = data.empresas;
        if (rawCompanies.length > 1) {
          setCompanies([
            { EMPRESAID: 'ALL', NOMBRE_EMPRESA: 'Todas las empresas' },
            ...rawCompanies,
          ]);
        } else {
          setCompanies(rawCompanies);
        }
      } else {
        setCompanies([]);
      }
    } catch (err) {
      console.error('❌ Error al obtener empresas dinámicamente:', err);
      setCompanies([]);
    }

    setAnchorElCompany(target);
  };

  const handleCompanyMenuClose = () => setAnchorElCompany(null);

  const handleChangeCompany = (event) => {
    handleMenuClose();
    handleQuickCompanyClick(event);
  };

  // SELECCIÓN DE EMPRESA
  const handleSelectCompany = (option) => {
    try {
      if (option === 'ALL' || (option && option.EMPRESAID === 'ALL')) {
        const ids = (companies || [])
          .map((c) => Number(c.EMPRESAID))
          .filter((id) => !isNaN(id) && id > 0);

        sessionStorage.setItem('EmpresaId', JSON.stringify(ids));
        sessionStorage.setItem('EmpresaNombre', 'TODAS');
      } else if (option && option.EMPRESAID) {
        const id = Number(option.EMPRESAID);
        sessionStorage.setItem('EmpresaId', JSON.stringify([id]));
        sessionStorage.setItem(
          'EmpresaNombre',
          option.NOMBRE_EMPRESA || option.nombre || ''
        );
      } else {
        console.warn('⚠️ handleSelectCompany opción inválida:', option);
        return;
      }

      // ✅ Registrar cambio de empresa en bitácora forense ISO 27001
      const selectedEmpresaNombre = sessionStorage.getItem('EmpresaNombre') || 'TODAS';
      const selectedEmpresaId = sessionStorage.getItem('EmpresaId') || '[]';
      const currentUser = sessionStorage.getItem('username') || sessionStorage.getItem('loggedInUsername') || 'Usuario';

      if (apiUrl) {
        axios.post(`${apiUrl}/api/auditoria/empresa-seleccionada`, {
          usuario: currentUser,
          empresaNombre: selectedEmpresaNombre,
          empresaId: selectedEmpresaId,
          accion: 'Cambio de Empresa Activa'
        }).catch(() => {});
      }

      // ✅ fuerza remonte del módulo actual aunque siga en la misma ruta
      setEmpresaRenderKey((prev) => prev + 1);

      // ✅ avisa a componentes escuchando cambio de empresa
      window.dispatchEvent(new Event('empresaCambiada'));

      setCompanyModalOpen(false);
      handleCompanyMenuClose();
      handleMenuClose();
      setEmpresaChangedAlert(true);
    } catch (e) {
      console.error('❌ Error al seleccionar empresa:', e);
    }
  };

  // Carga y sincronización de datos y permisos del usuario
  const syncUserPermissions = async () => {
    if (!isAuthenticated) return;

    // 1. Obtener ID de usuario y Perfil
    const uid = JSON.parse(sessionStorage.getItem('UsuarioId') || 'null');
    const pid = JSON.parse(sessionStorage.getItem('PerfilID') || 'null');

    let permisosActuales = sessionStorage.getItem('Permisos');

    if (uid || pid) {
      try {
        let nuevosPermisosArr = null;

        // Intentar consultar por usuario primero
        if (uid) {
          try {
            const res = await axios.get(`${apiUrl}/Permisos2/${uid}`);
            const permisosStr = res.data?.[0]?.PERMISOS ?? '';
            if (permisosStr) {
              nuevosPermisosArr = permisosStr.split(',').map(Number).filter(Boolean);
            }
          } catch (e) {}
        }

        // Si no se obtuvo por usuario específico, consultar por el perfil
        if (!nuevosPermisosArr && pid) {
          try {
            const res = await axios.get(`${apiUrl}/Permisos/${pid}`);
            const permisosStr = res.data?.permisos ?? '';
            if (permisosStr) {
              nuevosPermisosArr = permisosStr.split(',').map(Number).filter(Boolean);
            }
          } catch (e) {}
        }

        if (nuevosPermisosArr && Array.isArray(nuevosPermisosArr)) {
          // AUTO-GRANT regla de negocio: Permiso 1 -> Permiso 16
          if (nuevosPermisosArr.includes(1) && !nuevosPermisosArr.includes(16)) {
            nuevosPermisosArr.push(16);
          }
          const nuevaCadena = JSON.stringify(nuevosPermisosArr);
          if (nuevaCadena !== permisosActuales) {
            sessionStorage.setItem('Permisos', nuevaCadena);
            permisosActuales = nuevaCadena;
          }
        }
      } catch (err) {
        console.error('Error sincronizando permisos:', err);
      }
    }

    try {
      let parsed = JSON.parse(permisosActuales || '[]');
      if (!Array.isArray(parsed)) parsed = [parsed];
      parsed = parsed
        .map((permiso) => Number(permiso))
        .filter((permiso) => !Number.isNaN(permiso));

      const filtered = Items.filter((item) => parsed.includes(item.permisoId ?? item.id));
      setMenuItems(filtered);
    } catch (err) {
      console.error('Error parsing permisos:', err);
      setMenuItems([]);
    }

    setWelcomeMessage(sessionStorage.getItem('loggedInUsername') || '');
    setUserCorreo(JSON.parse(sessionStorage.getItem('correo') || '""'));
    setUserPerfil(JSON.parse(sessionStorage.getItem('PerfilNombre') || '""'));
    setCompanyName(sessionStorage.getItem('EmpresaNombre') || '');
  };

  useEffect(() => {
    syncUserPermissions();

    // Sincronizar en foco de ventana y por eventos
    const handleFocus = () => syncUserPermissions();
    const handlePermisosChange = () => syncUserPermissions();

    window.addEventListener('focus', handleFocus);
    window.addEventListener('permisosCambiados', handlePermisosChange);
    window.addEventListener('storage', handlePermisosChange);

    // Polling cada 30 segundos en segundo plano
    const timer = setInterval(syncUserPermissions, 30000);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('permisosCambiados', handlePermisosChange);
      window.removeEventListener('storage', handlePermisosChange);
      clearInterval(timer);
    };
  }, [isAuthenticated, usuarioId]);

  // Actualizar título
  useEffect(() => {
    const current = menuItems.find((item) => item.path === location.pathname);
    setTitulo(current ? current.title : 'Bienvenido');
  }, [location.pathname, menuItems]);

  // 🔔 CARGAR NOTIFICACIONES
  const fetchNotifications = async () => {
    if (!usuarioId) return;
    try {
      const resp = await axios.get(`${apiUrl}/api/notificaciones?usuarioId=${usuarioId}`);
      setNotificaciones(resp.data || []);
      setUnreadCount((resp.data || []).filter(n => !n.leida).length);
    } catch (e) {
      console.error("Error al cargar notificaciones:", e);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Refrescar cada minuto
    return () => clearInterval(interval);
  }, [usuarioId]);

  const handleOpenNotif = (event) => setAnchorElNotif(event.currentTarget);
  const handleCloseNotif = () => setAnchorElNotif(null);

  const handleMarkAsRead = async (id, toLink) => {
    try {
      await axios.post(`${apiUrl}/api/notificaciones/marcar-leida`, { id });
      fetchNotifications();
      if (toLink) {
        navigate(toLink);
        handleCloseNotif();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${apiUrl}/api/notificaciones/marcar-leida`, { todas: true, usuarioId });
      fetchNotifications();
    } catch (e) {
      console.error(e);
    }
  };

  // Cambios empresa (alerta verde)
  useEffect(() => {
    const actualizarEmpresa = () => {
      const nueva = sessionStorage.getItem('EmpresaNombre') || '';
      setCompanyName(nueva);
      setEmpresaChangedAlert(true);
    };

    actualizarEmpresa();
    window.addEventListener('empresaCambiada', actualizarEmpresa);

    return () => {
      window.removeEventListener('empresaCambiada', actualizarEmpresa);
    };
  }, []);

  useEffect(() => {
    if (!empresaChangedAlert) return;
    const timer = setTimeout(() => setEmpresaChangedAlert(false), 1000);
    return () => clearTimeout(timer);
  }, [empresaChangedAlert]);

  return (
    <>
      <Box sx={{ display: 'flex', minHeight: '100vh', width: '100vw', maxWidth: '100vw', overflowX: 'hidden', bgcolor: '#f4f7fb' }}>
        <CssBaseline />

        <AppBar position="fixed" elevation={0} dockWidth={dockWidth}>
          <Toolbar
            disableGutters
            sx={{
              minHeight: '40px !important',
              px: { xs: 1, sm: 1.5 },
              gap: { xs: 0.75, sm: 1 },
            }}
          >
            <IconButton
              color="inherit"
              onClick={() => setOpen((prev) => !prev)}
              edge="start"
              sx={{
                width: 32,
                height: 32,
                mr: { xs: 0.5, sm: 1 },
                display: { md: 'none' },
                color: '#0B1E36',
              }}
            >
              <MenuIcon sx={{ fontSize: 22 }} />
            </IconButton>

            <Typography 
              variant="h6" 
              noWrap 
              sx={{ 
                flexGrow: 1, 
                fontWeight: 700, 
                letterSpacing: '-0.2px',
                color: '#0f172a',
                fontSize: { xs: '0.84rem', sm: '0.92rem' },
                lineHeight: 1.2
              }}
            >
              {titulo}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              {/* 🕒 RELOJ EN TIEMPO REAL Y CONTADOR DE SESIÓN CON AUTO-LOGOUT */}
              <SessionGuard />

              {/* 🥢 SEPARADOR VERTICAL DE CRISTAL */}
              <Box sx={{ width: '1px', height: '16px', bgcolor: '#e2e8f0', display: { xs: 'none', sm: 'block' } }} />

              {/* 🔔 CAMPANA DE NOTIFICACIONES REFINADA (PUNTO 3) */}
              <IconButton 
                color="inherit" 
                onClick={handleOpenNotif} 
                sx={{ 
                  width: 28, 
                  height: 28, 
                  borderRadius: '7px',
                  color: '#334155',
                  bgcolor: Boolean(anchorElNotif) ? '#f1f5f9' : 'transparent',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: '#f1f5f9',
                    color: '#0655A5',
                  },
                }}
              >
                <Badge 
                  badgeContent={unreadCount} 
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.62rem',
                      height: 16,
                      minWidth: 16,
                      fontWeight: 800,
                    }
                  }}
                >
                  <NotificationsIcon sx={{ fontSize: 18 }} />
                </Badge>
              </IconButton>

              <Popover
                open={Boolean(anchorElNotif)}
                anchorEl={anchorElNotif}
                onClose={handleCloseNotif}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    width: 320,
                    maxHeight: 400,
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }
                }}
              >
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <Typography variant="subtitle1" fontWeight={800} color="#0B1E36">Avisos</Typography>
                  {unreadCount > 0 && (
                    <Typography 
                      variant="caption" 
                      sx={{ color: '#0655A5', cursor: 'pointer', fontWeight: 700 }}
                      onClick={handleMarkAllRead}
                    >
                      Marcar todo como leído
                    </Typography>
                  )}
                </Box>
                <List 
                  sx={{ 
                    p: 0, 
                    overflowY: 'auto', 
                    flexGrow: 1,
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
                    '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '10px' },
                    '&::-webkit-scrollbar-thumb:hover': { background: '#94a3b8' }
                  }}
                >
                  {notificaciones.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <NotificationsIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">No tienes avisos nuevos</Typography>
                    </Box>
                  ) : (
                    notificaciones.map((n) => {
                      const isAlert = n.tipo === 'ALERTA' || n.mensaje.includes('RECHAZADA');
                      const isSuccess = n.mensaje.includes('aprobada') || n.mensaje.includes('Autorizada');
                      
                      let IconComp = <InfoIcon sx={{ color: '#0655A5' }} />;
                      let barColor = '#cbd5e1';
                      if (isAlert) {
                        IconComp = <CancelIcon sx={{ color: '#d32f2f' }} />;
                        barColor = '#ef4444';
                      } else if (isSuccess) {
                        IconComp = <CheckCircleIcon sx={{ color: '#10b981' }} />;
                        barColor = '#10b981';
                      }

                      return (
                        <ListItem 
                          key={n.id} 
                          onClick={() => handleMarkAsRead(n.id, n.link)}
                          sx={{ 
                            borderBottom: '1px solid #f1f5f9', 
                            cursor: 'pointer',
                            position: 'relative',
                            pl: 2,
                            pr: 1.5,
                            py: 1, // Compacto
                            bgcolor: n.leida ? 'transparent' : 'rgba(6, 85, 165, 0.03)',
                            transition: 'all 0.2s',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.03)', '& .notif-icon': { transform: 'scale(1.1)' } },
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: '3px', // Más delgado
                              bgcolor: n.leida ? 'transparent' : barColor,
                              transition: 'all 0.2s'
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32, mr: 0.5 }} className="notif-icon">
                            {React.cloneElement(IconComp, { sx: { ...IconComp.props.sx, fontSize: 20, transition: 'transform 0.2s' } })}
                          </ListItemIcon>
                          <ListItemText 
                            primary={n.mensaje}
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                                <HistoryIcon sx={{ fontSize: 11, color: '#94a3b8' }} />
                                {new Date(n.fecha_creacion).toLocaleString('es-MX', { 
                                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
                                })}
                              </Box>
                            }
                            slotProps={{
                              primary: { 
                                sx: { 
                                  fontSize: '0.8rem', // Más chico
                                  fontWeight: n.leida ? 500 : 700, 
                                  color: n.leida ? '#64748b' : '#1e293b',
                                  lineHeight: 1.2
                                } 
                              },
                              secondary: { component: 'div' }
                            }}
                          />
                        </ListItem>
                      );
                    })
                  )}
                </List>
                {notificaciones.length > 0 && (
                  <Box sx={{ p: 1, textAlign: 'center', bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                      Mostrando avisos recientes
                    </Typography>
                  </Box>
                )}
              </Popover>

              {/* 🏢 SELECTOR RÁPIDO DE EMPRESA CON FLECHITA (PUNTO 2) */}
              {(() => {
                const currentStyle = getEmpresaColorStyle(companyName);
                const shortLabel = getShortEmpresaName(companyName);
                return (
                  <Button
                    onClick={handleQuickCompanyClick}
                    size="small"
                    variant="outlined"
                    startIcon={<BusinessIcon sx={{ fontSize: '15px !important', color: `${currentStyle.iconColor} !important` }} />}
                    endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '15px !important', color: `${currentStyle.iconColor} !important`, opacity: 0.8 }} />}
                    sx={{
                      height: 28,
                      fontWeight: 800,
                      fontSize: '0.76rem',
                      borderColor: currentStyle.borderColor,
                      bgcolor: currentStyle.bgcolor,
                      color: currentStyle.color,
                      textTransform: 'none',
                      borderRadius: '7px',
                      px: 1.2,
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        bgcolor: currentStyle.bgcolor,
                        borderColor: currentStyle.color,
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    {shortLabel}
                  </Button>
                );
              })()}

              {/* DESPLEGABLE RÁPIDO DE SELECCIÓN DE EMPRESA */}
              <Menu
                anchorEl={anchorElCompany}
                open={openCompanyMenu}
                onClose={handleCompanyMenuClose}
                disableRestoreFocus
                TransitionComponent={Grow}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflowX: 'hidden',
                    overflowY: 'visible',
                    boxShadow: '0 12px 28px -8px rgba(15, 23, 42, 0.15), 0 0 0 1px #e2e8f0',
                    mt: 1,
                    p: '8px',
                    minWidth: '220px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    bgcolor: '#ffffff',
                  },
                }}
              >
                <Typography sx={{ px: 1, py: 0.5, display: 'block', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.04em' }}>
                  Cambiar de empresa
                </Typography>
                <Divider sx={{ my: 0.5, borderColor: '#f1f5f9' }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, maxHeight: 260, overflowY: 'auto', overflowX: 'hidden' }}>
                  {companies.map((company) => {
                    const isAll = company.EMPRESAID === 'ALL';
                    const rawName = company.NOMBRE_EMPRESA || company.nombre || (isAll ? 'TODAS' : '');
                    const shortName = getShortEmpresaName(rawName);
                    const itemStyle = getEmpresaColorStyle(rawName);
                    return (
                      <MenuItem
                        key={company.EMPRESAID}
                        onClick={() => handleSelectCompany(company)}
                        sx={{
                          py: 0.8,
                          px: 1,
                          borderRadius: '6px',
                          fontSize: '0.78rem',
                          fontWeight: 700,
                          color: itemStyle.color,
                          bgcolor: itemStyle.bgcolor,
                          border: `1px solid ${itemStyle.borderColor}`,
                          gap: 1,
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            borderColor: itemStyle.color,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                          }
                        }}
                      >
                        {isAll ? <DomainIcon sx={{ fontSize: 16, color: itemStyle.iconColor }} /> : <BusinessIcon sx={{ fontSize: 16, color: itemStyle.iconColor }} />}
                        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {shortName}
                        </Box>
                        {isAll && (
                          <Chip label="Todas" size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 800, bgcolor: '#15803d', color: '#fff', px: 0.2 }} />
                        )}
                      </MenuItem>
                    );
                  })}
                </Box>
              </Menu>

              <IconButton onClick={handleMenuClick} sx={{ p: 0.25 }}>
                <Avatar sx={{ 
                  bgcolor: '#0655A5', 
                  color: '#ffffff', 
                  fontWeight: 800, 
                  width: 28,
                  height: 28,
                  fontSize: '0.82rem',
                  border: '1.5px solid #ffffff',
                  boxShadow: '0 2px 6px rgba(6, 85, 165, 0.3)',
                  transition: 'all 0.15s ease',
                  '&:hover': { transform: 'scale(1.06)' }
                }}>
                  {welcomeMessage?.charAt(0)?.toUpperCase() || '?'}
                </Avatar>
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleMenuClose}
                onClick={handleMenuClose}
                disableRestoreFocus
                TransitionComponent={Grow}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    boxShadow: '0 20px 40px -15px rgba(15, 23, 42, 0.18), 0 0 0 1px rgba(226, 232, 240, 0.95)',
                    mt: 1.25,
                    p: 'clamp(12px, 1.6vw, 16px)',
                    minWidth: 'clamp(230px, 20vw, 270px)',
                    maxWidth: 'clamp(250px, 22vw, 290px)',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    bgcolor: '#ffffff',
                    '&::before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 16,
                      width: 10,
                      height: 10,
                      bgcolor: '#ffffff',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                      borderTop: '1px solid #e2e8f0',
                      borderLeft: '1px solid #e2e8f0',
                    },
                  },
                }}
              >
                {/* Encabezado del Perfil */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
                  <Avatar
                    sx={{
                      width: 'clamp(36px, 2.5vw, 42px)',
                      height: 'clamp(36px, 2.5vw, 42px)',
                      background: 'linear-gradient(135deg, #0655A5 0%, #0284c7 100%)',
                      color: '#fff',
                      fontWeight: 800,
                      fontSize: 'clamp(0.92rem, 1vw, 1.1rem)',
                      boxShadow: '0 4px 12px rgba(6, 85, 165, 0.25)',
                      border: '2px solid #ffffff',
                    }}
                  >
                    {welcomeMessage?.charAt(0)?.toUpperCase() || '?'}
                  </Avatar>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2, fontSize: 'clamp(0.84rem, 0.8rem + 0.2vw, 0.94rem)' }} noWrap>
                      {welcomeMessage}
                    </Typography>
                    {userCorreo && (
                      <Tooltip title={userCorreo} arrow>
                        <Typography
                          sx={{ color: '#64748b', fontSize: 'clamp(0.68rem, 0.65rem + 0.1vw, 0.76rem)', mt: 0.2, fontWeight: 500, lineHeight: 1.15 }}
                          noWrap
                        >
                          {userCorreo}
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                {/* Info Empresa & Perfil */}
                <Box sx={{ bgcolor: '#f8fafc', borderRadius: '10px', p: 'clamp(8px, 1vw, 12px)', mb: 1.25, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ p: 0.35, bgcolor: '#eff6ff', borderRadius: '6px', display: 'flex', color: '#0284c7' }}>
                      <BusinessIcon sx={{ fontSize: 'clamp(13px, 0.9vw, 15px)' }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 'clamp(0.72rem, 0.7rem + 0.1vw, 0.78rem)' }} noWrap>
                      {companyName || 'Sin empresa seleccionada'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ p: 0.35, bgcolor: '#f1f5f9', borderRadius: '6px', display: 'flex', color: '#64748b' }}>
                      <AdminPanelSettingsIcon sx={{ fontSize: 'clamp(13px, 0.9vw, 15px)' }} />
                    </Box>
                    <Typography sx={{ color: '#64748b', fontSize: 'clamp(0.70rem, 0.68rem + 0.1vw, 0.76rem)', fontWeight: 600 }} noWrap>
                      {userPerfil}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 0.75, borderColor: '#f1f5f9' }} />

                {/* Acciones */}
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    setDispositivosModalOpen(true);
                  }}
                  sx={{
                    mb: 0.5,
                    px: 1.2,
                    py: 'clamp(6px, 0.7vw, 8px)',
                    borderRadius: '8px',
                    fontSize: 'clamp(0.76rem, 0.72rem + 0.1vw, 0.82rem)',
                    fontWeight: 600,
                    color: '#0f172a',
                    gap: 1,
                    transition: 'all 0.15s ease',
                    '&:hover': { backgroundColor: '#f0f7ff', color: '#0655A5', transform: 'translateX(2px)' },
                  }}
                >
                  <DevicesIcon sx={{ fontSize: 'clamp(15px, 1vw, 17px)', color: '#0655A5' }} />
                  Equipos de Confianza
                </MenuItem>

                <MenuItem
                  onClick={handleOpenConfirmationModal}
                  sx={{
                    mt: 0.25,
                    px: 1.2,
                    py: 'clamp(6px, 0.7vw, 8px)',
                    borderRadius: '8px',
                    fontSize: 'clamp(0.76rem, 0.72rem + 0.1vw, 0.82rem)',
                    fontWeight: 700,
                    color: '#dc2626',
                    gap: 1,
                    transition: 'all 0.15s ease',
                    '&:hover': { backgroundColor: '#fef2f2', color: '#b91c1c', transform: 'translateX(2px)' },
                  }}
                >
                  <LogoutIcon sx={{ fontSize: 'clamp(15px, 1vw, 17px)', color: '#dc2626' }} />
                  Cerrar Sesión
                </MenuItem>

                {empresaChangedAlert && (
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.5,
                      display: 'block',
                      textAlign: 'center',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: '#16a34a'
                    }}
                  >
                    Empresa actualizada ✔
                  </Typography>
                )}
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        <MuiDrawer
          variant={isDesktop ? 'permanent' : 'temporary'}
          open={isDesktop ? true : open}
          onClose={handleDrawerClose}
          ModalProps={{ keepMounted: true }}
          sx={(t) => {
            const mini = `calc(${t.spacing(7.5)} + 1px)`;
            const widthDesktop = open ? `${drawerWidth}px` : mini;

            return {
              width: isDesktop ? widthDesktop : `${drawerWidth}px`,
              flexShrink: 0,
              whiteSpace: 'nowrap',
              boxSizing: 'border-box',
              '& .MuiDrawer-paper': {
                width: isDesktop ? widthDesktop : `${drawerWidth}px`,
                boxSizing: 'border-box',
                backgroundColor: `${CHROME_BG} !important`,
                backgroundImage: 'none !important',
                color: '#1e293b',
                borderRight: '1px solid #d5dbdf',
                boxShadow: 'none',
                display: 'flex',
                flexDirection: 'column',
                overflowX: 'hidden',
                ...(isDesktop
                  ? (open ? openedMixin(t) : closedMixin(t))
                  : {}),
              },
            };
          }}
        >
          {/* Header del Sidebar — se fusiona visualmente con el Navbar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: open ? 'space-between' : 'center',
              px: open ? 1.8 : 0.8,
              py: 0,
              minHeight: 40,
              bgcolor: 'transparent',
            }}
          >
            {open ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    bgcolor: '#0655A5',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 900,
                    fontSize: 12,
                    boxShadow: '0 2px 8px rgba(6, 85, 165, 0.25)',
                    flexShrink: 0,
                  }}
                >
                  SP
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: '0.84rem', color: '#0f172a', letterSpacing: '-0.2px' }} noWrap>
                  Solicitudes de Pago
                </Typography>
              </Box>
            ) : null}

            <IconButton
              onClick={isDesktop ? () => setOpen((p) => !p) : handleDrawerClose}
              sx={{
                color: '#475569',
                p: 0.5,
                borderRadius: '8px',
                bgcolor: '#dbe1e8',
                border: '1px solid #cbd5e1',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#cbd5e1',
                  color: '#0655A5',
                  borderColor: '#94a3b8',
                },
              }}
            >
              {isDesktop ? (open ? <MenuOpenIcon sx={{ fontSize: 17 }} /> : <ChevronRightIcon sx={{ fontSize: 17 }} />) : <MenuOpenIcon sx={{ fontSize: 17 }} />}
            </IconButton>
          </Box>

          {/* Lista de Navegación Organizada en Acordeón Colapsable */}
          <List sx={{ px: open ? 1 : 0.6, py: 0.8, flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
            {(() => {
              // Definición de las 5 secciones con colores temáticos refinados
              const SECTIONS = [
                {
                  id: 'general',
                  label: 'General',
                  accentColor: '#0284c7', // Cyan / Azul Ejecutivo
                  icon: <DashboardCustomizeIcon sx={{ fontSize: 16, color: '#0284c7' }} />,
                  ids: [12], // Dashboard
                },
                {
                  id: 'solicitudes',
                  label: 'Solicitudes',
                  accentColor: '#2563eb', // Azul Royal / Zafiro
                  icon: <AssignmentIcon sx={{ fontSize: 16, color: '#2563eb' }} />,
                  ids: [1, 2, 17, 3, 21, 19], // Órdenes de Compra, Por Autorizar, Solicitudes Vencidas, Historial, Panel de Gestión, Reporte para Tesorería
                },
                {
                  id: 'pagos',
                  label: 'Pagos y Tesorería',
                  accentColor: '#059669', // Verde Esmeralda Financiero
                  icon: <PaymentsIcon sx={{ fontSize: 16, color: '#059669' }} />,
                  ids: [7, 8], // Pagos Pendientes, Solicitudes Pagadas
                },
                {
                  id: 'reportes',
                  label: 'Reportes y Auditoría',
                  accentColor: '#7c3aed', // Púrpura / Violeta
                  icon: <BarChartIcon sx={{ fontSize: 16, color: '#7c3aed' }} />,
                  ids: [18, 10], // Seguimiento de Solicitudes, Bitácora
                },
                {
                  id: 'catalogos',
                  label: 'Configuración',
                  accentColor: '#d97706', // Ámbar / Dorado
                  icon: <SettingsIcon sx={{ fontSize: 16, color: '#d97706' }} />,
                  ids: [25, 6, 5, 11, 13, 14, 15, 35, 36], // Carga Exhibits, Usuarios, Niveles, Perfiles, CCs, Departamentos, Proveedores, Funciones, Sincronización BD
                },
              ];

              // Recopilar todos los IDs declarados en secciones
              const allSectionIds = new Set(SECTIONS.flatMap(s => s.ids));

              return SECTIONS.map((sec, secIdx) => {
                // Filtrar los items asignados a esta sección + items no clasificados si es la sección de configuración
                const itemsInSec = menuItems.filter(item => {
                  if (sec.ids.includes(item.id)) return true;
                  if (sec.id === 'catalogos' && !allSectionIds.has(item.id)) return true;
                  return false;
                });
                if (itemsInSec.length === 0) return null;

                const isExpanded = expandedSections[sec.id] ?? true;
                const hasActiveChild = itemsInSec.some(item => location.pathname === item.path);

                return (
                  <Box key={sec.id} sx={{ mb: open ? 0.8 : 0.5 }}>
                    {/* Encabezado de Sección en modo expandido */}
                    {open ? (
                      <Box
                        onClick={() => toggleSection(sec.id)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 1,
                          py: 0.5,
                          mt: secIdx === 0 ? 0.2 : 1.2,
                          mb: 0.3,
                          cursor: 'pointer',
                          userSelect: 'none',
                          color: hasActiveChild ? sec.accentColor : '#64748b',
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            color: '#0f172a',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {sec.icon}
                          </Box>
                          <Typography
                            sx={{
                              fontSize: '0.66rem',
                              fontWeight: 800,
                              textTransform: 'uppercase',
                              letterSpacing: '0.06em',
                              color: 'inherit',
                              lineHeight: 1,
                            }}
                          >
                            {sec.label}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', opacity: 0.6 }}>
                          {isExpanded ? <ExpandLess sx={{ fontSize: 15 }} /> : <ExpandMore sx={{ fontSize: 15 }} />}
                        </Box>
                      </Box>
                    ) : (
                      secIdx > 0 && (
                        <Box sx={{ my: 0.6, mx: 0.5, borderTop: '1px solid #e2e8f0' }} />
                      )
                    )}

                    {/* Contenido colapsable */}
                    <Collapse in={open ? isExpanded : true} timeout="auto" unmountOnExit>
                      <Box sx={{ pt: 0.2 }}>
                        {itemsInSec.map((item) => {
                          const isActive = location.pathname === item.path;

                          const itemNode = (
                            <ListItem
                              key={item.id}
                              disablePadding
                              onClick={() => handleItemClick(item.path)}
                              sx={{ display: 'block', mb: 0.2 }}
                            >
                              <ListItemButton
                                sx={{
                                  minHeight: 30,
                                  justifyContent: isDesktop ? (open ? 'initial' : 'center') : 'initial',
                                  px: open ? 1 : 0.8,
                                  py: 0.4,
                                  borderRadius: '6px',
                                  position: 'relative',
                                  transition: 'all 0.12s ease',
                                  backgroundColor: isActive ? '#0655A5' : 'transparent',
                                  color: isActive ? '#ffffff' : '#334155',
                                  boxShadow: isActive ? '0 2px 6px rgba(6, 85, 165, 0.22)' : 'none',
                                  '&:hover': {
                                    backgroundColor: isActive ? '#054785' : '#f1f5f9',
                                    color: isActive ? '#ffffff' : '#0f172a',
                                    '& .MuiListItemIcon-root': {
                                      color: isActive ? '#ffffff' : sec.accentColor,
                                    }
                                  },
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    color: isActive ? '#ffffff' : '#64748b',
                                    minWidth: 0,
                                    mr: isDesktop ? (open ? 1 : 'auto') : 1,
                                    justifyContent: 'center',
                                    display: 'flex',
                                    alignItems: 'center',
                                    transition: 'color 0.12s ease',
                                    '& svg': {
                                      fontSize: 16,
                                    },
                                  }}
                                >
                                  {item.icon}
                                </ListItemIcon>

                                <ListItemText
                                  primary={item.title}
                                  sx={{
                                    opacity: isDesktop ? (open ? 1 : 0) : 1,
                                    m: 0,
                                  }}
                                  slotProps={{
                                    primary: {
                                      sx: {
                                        fontSize: '0.75rem',
                                        fontWeight: isActive ? 700 : 500,
                                        color: isActive ? '#ffffff' : '#334155',
                                        lineHeight: 1.2,
                                      }
                                    },
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );

                          return (
                            <React.Fragment key={item.id}>
                              {isDesktop && !open ? (
                                <Tooltip
                                  title={item.title}
                                  placement="right"
                                  arrow
                                  disableInteractive
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        bgcolor: '#0f172a',
                                        color: '#ffffff',
                                        fontWeight: 700,
                                        fontSize: '0.72rem',
                                        px: 1.2,
                                        py: 0.5,
                                        borderRadius: '6px',
                                        boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
                                      }
                                    },
                                    arrow: {
                                      sx: { color: '#0f172a' }
                                    }
                                  }}
                                >
                                  {itemNode}
                                </Tooltip>
                              ) : (
                                itemNode
                              )}
                            </React.Fragment>
                          );
                        })}
                      </Box>
                    </Collapse>
                  </Box>
                );
              });
            })()}
          </List>
        </MuiDrawer>

        {/* Contenido */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            width: 0,
            px: { xs: 1, sm: 1.5 },
            pb: { xs: 1, sm: 1.5 },
            pt: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            maxHeight: '100vh',
            overflow: 'hidden',
          }}
        >
          <DrawerHeader />

          <Paper
            key={empresaRenderKey}
            elevation={0}
            sx={{
              p: { xs: 0.5, sm: 1 },
              borderRadius: { xs: 1.5, sm: 2 },
              border: '1px solid #e2e8f0',
              backgroundColor: '#ffffff',
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Outlet />
          </Paper>
        </Box>
      </Box>

      <CompanySelectorModal
        open={companyModalOpen}
        onClose={() => setCompanyModalOpen(false)}
        companies={companies}
        onSelect={handleSelectCompany}
      />

      <ConfirmationModal
        open={confirmationModalOpen}
        handleConfirm={handleLogout}
        handleCancel={handleCloseConfirmationModal}
      />

      <ModalDispositivosSeguros
        open={dispositivosModalOpen}
        onClose={() => setDispositivosModalOpen(false)}
        usuarioId={
          JSON.parse(sessionStorage.getItem('usuarioID') || 'null') ||
          JSON.parse(sessionStorage.getItem('UsuarioId') || 'null')
        }
        nombreUsuario={sessionStorage.getItem('loggedInUsername')}
      />
    </>
  );
}
